import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

type ReleaseType = 'major' | 'minor' | 'patch'
type AudienceType = 'customers' | 'internal'

type ReleaseMeta = {
  version: string
  date: string
  type: ReleaseType
  audience: AudienceType
  summary: string
}

type ReleaseNote = {
  slug: string
  meta: ReleaseMeta
  body: string
}

function parseFrontmatter(markdown: string): { meta: ReleaseMeta; body: string } | null {
  const normalized = markdown.replace(/\r\n/g, '\n')
  const match = normalized.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/)
  if (!match) {
    return null
  }

  const rawMeta = match[1] ?? ''
  const body = match[2] ?? ''
  const metaMap = new Map<string, string>()

  for (const line of rawMeta.split('\n')) {
    const separatorIndex = line.indexOf(':')
    if (separatorIndex === -1) {
      continue
    }
    const key = line.slice(0, separatorIndex).trim()
    const value = line
      .slice(separatorIndex + 1)
      .trim()
      .replace(/^['"]|['"]$/g, '')
    metaMap.set(key, value)
  }

  const version = metaMap.get('version')
  const date = metaMap.get('date')
  const type = metaMap.get('type')
  const audience = metaMap.get('audience')
  const summary = metaMap.get('summary')

  if (!version || !date || !summary) {
    return null
  }

  if (type !== 'major' && type !== 'minor' && type !== 'patch') {
    return null
  }

  if (audience !== 'customers' && audience !== 'internal') {
    return null
  }

  return {
    meta: { version, date, type, audience, summary },
    body: body.trim(),
  }
}

const releaseModules = import.meta.glob('../content/updates/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>

const releaseNotes: ReleaseNote[] = Object.entries(releaseModules)
  .map(([path, content]) => {
    const parsed = parseFrontmatter(content)
    if (!parsed) {
      return null
    }

    const filename = path.split('/').pop() ?? path
    const slug = filename.replace(/\.md$/, '')

    return {
      slug,
      meta: parsed.meta,
      body: parsed.body,
    }
  })
  .filter((note): note is ReleaseNote => note !== null)
  .sort((a, b) => new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime())

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function UpdatesPage() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MarketingNav variant="standalone" />
      <main className="container max-w-5xl px-4 pt-32 pb-16 space-y-10">
        <section className="space-y-3">
          <h1 className="text-4xl font-medium text-slate-900">Product Updates</h1>
          <p className="text-slate-600">
            Release notes for customer-facing improvements, fixes, and reliability updates.
          </p>
        </section>

        {releaseNotes.length === 0 ? (
          <section className="rounded-xl border border-slate-200 p-6">
            <p className="text-slate-600">No release notes published yet.</p>
          </section>
        ) : (
          <section className="space-y-6">
            {releaseNotes.map((note) => (
              <details
                key={note.slug}
                id={note.slug}
                className="group rounded-xl border border-slate-200 bg-white p-6"
              >
                <summary className="list-none cursor-pointer space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                        v{note.meta.version}
                      </span>
                      <span className="inline-flex rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                        {note.meta.type}
                      </span>
                      <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                        {note.meta.audience}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-slate-600 group-open:hidden">
                      View details
                    </span>
                    <span className="hidden text-sm font-medium text-slate-600 group-open:inline">
                      Hide details
                    </span>
                  </div>
                  <p className="text-sm text-slate-500">{formatDate(note.meta.date)}</p>
                  <p className="text-lg font-semibold text-slate-900">{note.meta.summary}</p>
                </summary>

                <div className="mt-5 border-t border-slate-100 pt-5">
                  <div className="prose prose-slate max-w-none prose-headings:mt-4 prose-headings:mb-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.body}</ReactMarkdown>
                  </div>
                </div>
              </details>
            ))}
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  )
}
